import {
    ITimeRangeCreateRequest,
    ITimeRangeResponse,
} from '@kcftech/prometheustypescriptapiclient';
import { TableColumn } from 'components/data_table/types';
import { DocumentTitle } from 'components/DocumentTitle';
import { ConfirmModal } from 'components/modal/ConfirmModal';
import { TablePageLayout } from 'components/page/styled';
import { CRUDTable } from 'components/table/CRUDTable';
import { sdClient } from 'contexts/SDKClientContext';
import {
    TimeRangeFilter,
    usePagedTimeRangeDataSource,
} from 'custom_hooks/data_hooks/data_source/TimeRangesDataSource';
import { useCreateTimeRange } from 'custom_hooks/data_hooks/time_ranges/useCreateTimeRange';
import { CreateTimeRangeModal } from 'pages/Settings/TimeRanges/CreateTimeRangeModal';
import { TimeRangeDescriptionCell } from 'pages/Settings/TimeRanges/TimeRangeDescriptionCell';
import { TimeRangeIsGlobalCell } from 'pages/Settings/TimeRanges/TimeRangeIsGlobalCell';
import { TimeRangeNameCell } from 'pages/Settings/TimeRanges/TimeRangeNameCell';
import { TimeRangeTimeCell } from 'pages/Settings/TimeRanges/TimeRangeTimeCell';
import { useMemo, useState } from 'react';
import { queryClient } from 'utils/query';

const timeRangeTableQueryName = 'time-ranges-table';
export type TimeRangeTableCellProps = Readonly<{
    rowData: ITimeRangeResponse;
    onChange: () => void;
    className?: string;
}>;

export function TimeRanges() {
    const [selectedRange, setSelectedRange] = useState<ITimeRangeResponse | null>(null);
    const [deleteOpen, setDeleteOpen] = useState(false);
    const [createModalOpen, setCreateModalOpen] = useState(false);
    const dataSource = usePagedTimeRangeDataSource({
        name: timeRangeTableQueryName,
        defaultFilter: {
            nameSearch: undefined,
        },
        defaultSort: [
            {
                field: 'TimeInSeconds',
                dir: 'asc',
            },
        ],
    });

    const { mutateAsync: createTimeRange } = useCreateTimeRange({
        queryName: timeRangeTableQueryName,
    });

    const onCreateConfirm = async (request: ITimeRangeCreateRequest) => {
        await createTimeRange({ ...request });
        setCreateModalOpen(false);
    };

    const onDeleteConfirm = async () => {
        if (selectedRange) {
            await sdClient.deleteRange({ rangeId: selectedRange.Id });
            setSelectedRange(null);
            setDeleteOpen(false);
            queryClient.invalidateQueries({
                queryKey: [timeRangeTableQueryName],
            });
        }
    };

    const columns = useMemo<TableColumn<ITimeRangeResponse>[]>(
        () => [
            {
                header: () => 'Button Text',
                id: 'Name',
                value: ({ rowData }) => (
                    <TimeRangeNameCell
                        rowData={rowData}
                        onChange={dataSource.reloadPage}
                    />
                ),
                primary: true,
            },
            {
                header: () => 'Time',
                id: 'TimeInSeconds',
                value: ({ rowData }) => (
                    <TimeRangeTimeCell
                        rowData={rowData}
                        onChange={dataSource.reloadPage}
                    />
                ),
                sortOn: 'TimeInSeconds',
            },
            {
                header: () => 'Tooltip Description',
                id: 'Description',
                value: ({ rowData }) => (
                    <TimeRangeDescriptionCell
                        rowData={rowData}
                        onChange={dataSource.reloadPage}
                    />
                ),
            },
            {
                header: () => 'Add Automatically',
                id: 'IsGlobal',
                value: ({ rowData }) => (
                    <TimeRangeIsGlobalCell
                        rowData={rowData}
                        onChange={dataSource.reloadPage}
                    />
                ),
            },
        ],
        [dataSource.reloadPage]
    );

    return (
        <TablePageLayout>
            <DocumentTitle title="Time Range Settings" />
            <CRUDTable<ITimeRangeResponse, TimeRangeFilter>
                columns={columns}
                dataSource={dataSource}
                onCreate={() => setCreateModalOpen(true)}
                onDelete={(range) => {
                    setSelectedRange(range);
                    setDeleteOpen(true);
                }}
                rowKey={(record) => `${record.Id}`}
                noResultMessage="No time range found"
            />
            {createModalOpen && (
                <CreateTimeRangeModal
                    onConfirm={onCreateConfirm}
                    onClose={() => setCreateModalOpen(false)}
                />
            )}
            {selectedRange && deleteOpen && (
                <ConfirmModal
                    title={`Delete '${selectedRange.Name}'`}
                    actionText="delete this time range?"
                    checkText="delete this time range"
                    actionButtonText="Delete"
                    onAction={onDeleteConfirm}
                    onClose={() => setDeleteOpen(false)}
                />
            )}
        </TablePageLayout>
    );
}
